import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { openWebCenterDecisionCall } from '@/lib/analytics';
import {
  ArrowRight,
  CalendarClock,
  ShieldCheck,
  KeyRound,
  Users,
  FolderTree,
  Network,
  Server,
  Workflow,
  ListChecks,
  Fingerprint,
} from 'lucide-react';
import Navigation from '@/components/Navigation';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import WebCenterEngagementHistory from '@/components/WebCenterEngagementHistory';
import LastUpdated from '@/components/LastUpdated';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { FOUNDER, founderPersonLd } from '@/lib/founder';

const PAGE_TITLE = 'Oracle Identity & Access Management 12c to 14c Upgrade | EZ Cloud';
const PAGE_DESCRIPTION =
  'Oracle Identity & Access Management (IAM) 14c (14.1.2.1) was released March 2025 — Access Manager, Identity Governance, Unified Directory, and Internet Directory, on WebLogic 14c. For IAM 12c customers facing the December 2026 Fusion Middleware deadline, the 14c upgrade preserves the identity estate and buys roughly 8 years of runway. Upgrade patterns, component coverage, and how EZ Cloud delivers.';
const CANONICAL = 'https://ezcloud.ai/solutions/oracle-identity-management-12c-to-14c-upgrade';

const SCHEMA_ABOUT = [
  'Oracle Identity and Access Management',
  'Oracle Access Management',
  'Oracle Identity Governance',
  'Oracle Unified Directory',
  'Oracle Internet Directory',
  'Oracle IAM 14c',
];

const KEY_DATES = [
  {
    date: 'December 2026',
    label: 'Fusion Middleware 12c Premier Support ends',
    detail:
      'Oracle Fusion Middleware 12c (12.2.1.4) — the platform underneath Identity & Access Management 12c — reaches the end of Premier Support on the normal Lifetime Support schedule. New IAM 12c customers plan their upgrade around this window.',
    severity: 'amber',
  },
  {
    date: 'December 2027',
    label: 'Fusion Middleware 12c Extended Support ends',
    detail:
      'Per Oracle\'s Lifetime Support Policy, Extended Support for Fusion Middleware 12c (12.2.1.4) runs through December 2027, providing security and critical patches during the upgrade window.',
    severity: 'red',
  },
  {
    date: 'March 2025 → ~2033',
    label: 'IAM 14c (14.1.2.1) support window',
    detail:
      'Oracle Identity & Access Management 14c (14.1.2.1) was released March 2025 and carries approximately 8 years of combined Premier and Extended Support, then indefinite Sustaining Support. The 12c → 14c upgrade preserves the identity estate and resets the support clock.',
    severity: 'green',
  },
];

const COMPONENTS_14C = [
  {
    icon: KeyRound,
    title: 'Oracle Access Management (OAM)',
    detail:
      'Single sign-on, authentication, authorization, and risk-aware access. The web and application access tier — WebGates, policies, agents, and federation — carries forward into 14c on WebLogic Server 14c.',
  },
  {
    icon: Users,
    title: 'Oracle Identity Governance (OIG)',
    detail:
      'Identity lifecycle, provisioning, access request and certification, and connector-based reconciliation with target systems. Governance workflows, approval policies, and connectors are validated against the 14c release.',
  },
  {
    icon: FolderTree,
    title: 'Oracle Unified Directory (OUD)',
    detail:
      'The elastic LDAP directory and storage/proxy tier. Directory data, replication topology, and OUD-backed identity stores are re-established and validated in the 14c estate.',
  },
  {
    icon: Network,
    title: 'Oracle Internet Directory (OID)',
    detail:
      'The database-backed LDAP directory. OID data and dependent integrations are upgraded and revalidated as part of the 14c directory tier alongside — or in place of — OUD.',
  },
];

const UPGRADE_PATH = [
  {
    step: '1',
    title: 'Confirm the 12c PS4 baseline',
    body: 'Upgrades to 14c (14.1.2) are supported from 12.2.1.4 (12c PS4). Identity & Access Management estates on earlier 12c updates move to 12.2.1.4 first, then forward to 14c. The Inventory & Assessment step confirms the current patch baseline for OAM, OIG, OUD, and OID.',
  },
  {
    step: '2',
    title: 'Read the existing identity estate',
    body: 'Document every OAM SSO integration and agent, OIG connector and customization, OUD/OID directory tree, replication topology, and downstream dependency (EBS, Fusion, WebCenter, custom apps). Surface anything that needs different handling in 14c before any change is made.',
  },
  {
    step: '3',
    title: 'Stand up WebLogic 14c and the 14c binaries',
    body: 'IAM 14c runs on Oracle WebLogic Server 14c, certified with Oracle Database 19c/23c/23ai and JDK 17 (also certified with JDK 21). The 14c middleware tier is established — see our WebLogic 14c upgrade page for the server-tier detail — as the foundation for the identity components.',
    weblogicLink: true,
  },
  {
    step: '4',
    title: 'Upgrade schemas and configuration (in-place or out-of-place)',
    body: 'The 12c PS4 → 14c upgrade is supported both in-place and out-of-place. Out-of-place builds a new 14c environment alongside the running 12c estate for lower cutover risk; in-place upgrades the existing Oracle Home. Schemas are upgraded via the Upgrade Assistant and validated against 14c.',
  },
  {
    step: '5',
    title: 'Validate integrations and directory data',
    body: 'Re-test OAM SSO and federation flows, OIG provisioning/reconciliation and approval workflows, and OUD/OID directory reads, writes, and replication. Confirm every downstream application authenticates and authorizes correctly against the 14c estate.',
  },
  {
    step: '6',
    title: 'Parallel run, UAT, cutover',
    body: 'Run 12c and 14c in parallel through UAT where an out-of-place approach is used. Validate every SSO path, governance workflow, and directory dependency before cutover, and keep a tested rollback position throughout.',
  },
];

const COMPONENT_DETAIL = [
  {
    icon: KeyRound,
    component: 'Oracle Access Management (OAM)',
    what: 'What the upgrade means',
    detail:
      'SSO integrations, WebGate/agent registrations, authentication schemes, authorization policies, and federation partnerships are re-established on OAM 14c. Risk-aware and adaptive authentication configuration is validated. The goal is that every application that trusted 12c OAM authenticates identically against 14c.',
  },
  {
    icon: Workflow,
    component: 'Oracle Identity Governance (OIG)',
    what: 'What the upgrade means',
    detail:
      'Governance workflows — access request, approval, certification, and reconciliation — plus target-system connectors and any OIG customizations are migrated and validated against the 14c release. Connector versions are checked for 14c certification, and custom event handlers, adapters, and UI customizations are reviewed for compatibility.',
  },
  {
    icon: FolderTree,
    component: 'Oracle Unified Directory & Internet Directory (OUD / OID)',
    what: 'What the upgrade means',
    detail:
      'Directory data, schema, and access control are carried into the 14c directory tier, and replication topology is re-established and validated end to end. Whether the estate runs OUD, OID, or both, the upgrade confirms directory reads/writes and replication behave identically before dependent SSO and governance flows are cut over.',
  },
];

const DELIVERY_STEPS = [
  {
    icon: ListChecks,
    title: 'Read the existing IAM estate',
    body: 'Andrew reads your running Identity & Access Management implementation directly — OAM SSO integrations, OIG connectors and customizations, OUD/OID directory topology, and every downstream dependency — and produces a structured 12c → 14c upgrade plan.',
  },
  {
    icon: Server,
    title: 'Out-of-place 14c build',
    body: 'A new 14c environment on WebLogic Server 14c is stood up alongside the running 12c estate, so the current identity services stay available while the 14c components are built and configured.',
  },
  {
    icon: Fingerprint,
    title: 'Integration and directory validation',
    body: 'SSO and federation flows, governance workflows and connectors, and directory replication are validated component by component against the 14c estate before any traffic is moved.',
  },
  {
    icon: ShieldCheck,
    title: 'Cutover with a tested rollback',
    body: 'Cutover happens only after end-to-end authentication, authorization, provisioning, and directory testing passes — with the 12c estate held as a tested rollback position throughout.',
  },
];

const FAQS = [
  {
    q: 'Is Oracle Identity & Access Management 14c real, and when was it released?',
    a: 'Yes. Oracle Identity & Access Management 14c is release 14.1.2.1, released in March 2025. It includes Oracle Access Management (OAM), Oracle Identity Governance (OIG), Oracle Unified Directory (OUD), and Oracle Internet Directory (OID), running on Oracle WebLogic Server 14c. It carries approximately 8 years of combined Premier and Extended Support from release, then indefinite Sustaining Support.',
  },
  {
    q: 'Which components are included in IAM 14c?',
    a: 'IAM 14c (14.1.2.1) covers the four core Oracle identity components: Oracle Access Management (OAM) for SSO, authentication, authorization, and risk-aware access; Oracle Identity Governance (OIG) for lifecycle, provisioning, and certification; Oracle Unified Directory (OUD) for the elastic LDAP directory tier; and Oracle Internet Directory (OID) for the database-backed LDAP directory. All four run on WebLogic Server 14c and are certified with Oracle Database 19c/23c/23ai and JDK 17 (also certified with JDK 21).',
  },
  {
    q: 'What is the 12c to 14c upgrade path for IAM?',
    a: 'The supported path is from Identity & Access Management 12c PS4 (on Fusion Middleware 12.2.1.4) to 14c (14.1.2.1). Estates on earlier 12c updates move to 12.2.1.4 first, then upgrade to 14c. The 12c PS4 → 14c upgrade is supported both in-place, upgrading the existing Oracle Home, and out-of-place, building a new 14c environment alongside the running 12c estate for lower cutover risk.',
  },
  {
    q: 'Why does IAM 14c depend on WebLogic 14c?',
    a: 'Oracle Identity & Access Management 14c runs on Oracle WebLogic Server 14c as its application-server foundation, and is certified with Oracle Database 19c/23c/23ai and JDK 17 (also certified with JDK 21). Standing up the WebLogic 14c tier is a prerequisite step in the IAM upgrade — see our Oracle WebLogic 14c upgrade page for the server-tier detail.',
  },
  {
    q: 'What are the directory replication considerations in an OUD / OID upgrade?',
    a: 'Directory replication is one of the areas that needs explicit validation during a 12c → 14c upgrade. The replication topology across Oracle Unified Directory or Oracle Internet Directory nodes is re-established in the 14c estate, and reads, writes, and replication convergence are tested end to end before dependent SSO and governance flows are cut over. Confirming directory data and replication behave identically to 12c is a gating step, not an afterthought.',
  },
  {
    q: 'How are OIG connectors and customizations migrated?',
    a: 'Oracle Identity Governance connectors are checked for 14c certification, and any customizations — custom event handlers, adapters, approval workflows, and UI extensions — are reviewed for compatibility with the 14c release. Governance workflows for access request, approval, certification, and reconciliation are re-established and validated against target systems. The assessment phase surfaces which connectors and customizations transfer directly and which need re-work before cutover.',
  },
  {
    q: 'How long does a 12c to 14c IAM upgrade take?',
    a: 'Timelines depend on the size and complexity of the identity estate — the number of OAM SSO integrations, OIG connectors and customizations, and the directory topology across OUD and OID. Estates close to Oracle\'s reference configuration move faster; those with extensive governance customizations and many integrated applications take longer. The Inventory & Assessment step, typically a few weeks, surfaces the right timeline for your specific implementation.',
  },
  {
    q: 'Does upgrading to IAM 14c really buy about 8 years of runway?',
    a: 'Yes. Oracle Identity & Access Management 14c (14.1.2.1) was released March 2025 and carries approximately 8 years of combined Premier and Extended Support under Oracle\'s Lifetime Support Policy, followed by indefinite Sustaining Support. Upgrading the identity estate from 12c to 14c preserves the OAM, OIG, OUD, and OID investment and resets the support clock, rather than replacing the identity platform.',
  },
];

const OracleIdentityManagement12cTo14cUpgrade = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  const personSchema = { '@context': 'https://schema.org', ...founderPersonLd };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    headline: PAGE_TITLE,
    description: PAGE_DESCRIPTION,
    author: { '@id': 'https://ezcloud.ai/about#andrew-blackman' },
    publisher: { '@id': 'https://ezcloud.ai/#organization' },
    datePublished: '2026-07-06',
    dateModified: '2026-07-06',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': CANONICAL,
    },
    about: SCHEMA_ABOUT.map((name) => ({ '@type': 'Thing', name })),
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={PAGE_TITLE}
        description={PAGE_DESCRIPTION}
        canonical={CANONICAL}
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          {
            name: 'Solutions',
            url: 'https://ezcloud.ai/solutions/oracle-fusion-middleware-12c-end-of-support',
          },
          {
            name: 'Oracle IAM 12c to 14c Upgrade',
            url: CANONICAL,
          },
        ]}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(personSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Navigation />
      <Breadcrumbs />
      <LastUpdated date="2026-07-06" />

      <main>
        {/* Hero */}
        <section className="bg-hero-gradient text-white pt-16 pb-20 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-br from-primary/20 to-transparent" />
            <div className="absolute top-20 right-20 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
          </div>
          <div className="container mx-auto px-4 max-w-5xl relative">
            <div className="flex items-center gap-2 text-accent text-sm font-semibold uppercase tracking-wide mb-4">
              <ShieldCheck className="w-4 h-4" aria-hidden />
              Oracle IAM 14c · Access Manager · Identity Governance
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Oracle Identity &amp; Access Management 12c to 14c upgrade
              <span className="block text-accent mt-3 text-2xl md:text-3xl lg:text-4xl font-bold">
                IAM 14c (14.1.2.1) shipped March 2025 — preserve the identity estate, reset the
                support clock.
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-white/80 max-w-3xl mb-8">
              Oracle Identity &amp; Access Management 14c (14.1.2.1) shipped March 2025 — Access
              Manager, Identity Governance, Unified Directory, and Internet Directory, on WebLogic
              14c. Preserve the identity estate and buy approximately 8 years of runway off the
              December 2026 Fusion Middleware deadline.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('iam-14c-hero')}
              >
                <CalendarClock className="mr-2 w-5 h-5" aria-hidden />
                Book an IAM Upgrade Decision Call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/solutions/oracle-fusion-middleware-12c-end-of-support">
                  See the full Fusion Middleware 12c guide
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* TL;DR — definitional summary for AEO synthesis */}
        <section className="py-14 bg-white border-b border-gray-100">
          <div className="container mx-auto px-4 max-w-4xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
              What this is
            </p>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-5 leading-tight">
              Oracle IAM 12c to 14c upgrade, defined
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                <strong className="text-gray-900">
                  Oracle Identity &amp; Access Management 14c (release 14.1.2.1) was released in
                  March 2025 and covers the four core Oracle identity components — Oracle Access
                  Management (OAM), Oracle Identity Governance (OIG), Oracle Unified Directory (OUD),
                  and Oracle Internet Directory (OID).
                </strong>{' '}
                All four run on Oracle WebLogic Server 14c and are certified with Oracle Database
                19c/23c/23ai and JDK 17 (also certified with JDK 21).
              </p>
              <p>
                The supported upgrade path runs from{' '}
                <strong className="text-gray-900">
                  Identity &amp; Access Management 12c PS4 (on Fusion Middleware 12.2.1.4) to 14c
                </strong>
                , and is supported both in-place, upgrading the existing Oracle Home, and
                out-of-place, building a new 14c environment alongside the running 12c estate for
                lower cutover risk. Estates on earlier 12c updates move to 12.2.1.4 first.
                Underneath IAM 14c sits{' '}
                <Link to="/solutions/oracle-weblogic-14c-upgrade" className="text-primary hover:text-accent font-semibold">
                  WebLogic Server 14c
                </Link>
                , so the server tier is upgraded as part of the same program.
              </p>
              <p>
                Oracle Fusion Middleware 12c (12.2.1.4) — the platform underneath IAM 12c — reaches
                the end of Premier Support in December 2026, with Extended Support through December
                2027, on the normal{' '}
                <Link to="/solutions/oracle-fusion-middleware-12c-end-of-support" className="text-primary hover:text-accent font-semibold">
                  Lifetime Support schedule
                </Link>
                . IAM 14c carries approximately 8 years of combined Premier and Extended Support
                from its March 2025 release, then indefinite Sustaining Support. The 12c → 14c
                upgrade preserves the OAM, OIG, OUD, and OID investment and resets the support clock.
              </p>
            </div>
          </div>
        </section>

        {/* Key dates */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              The timeline
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Key dates for the IAM 12c → 14c decision
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Per Oracle's Lifetime Support Policy. The Fusion Middleware 12c support window and the
              14c release window together frame the decision for every Identity &amp; Access
              Management customer.
            </p>
            <div className="space-y-5">
              {KEY_DATES.map((d) => (
                <Card
                  key={d.date}
                  className={`p-7 bg-white border shadow-sm ${
                    d.severity === 'red'
                      ? 'border-red-200'
                      : d.severity === 'amber'
                        ? 'border-amber-200'
                        : 'border-green-200'
                  }`}
                >
                  <div className="flex flex-col sm:flex-row gap-5 items-start">
                    <div
                      className={`flex-shrink-0 px-4 py-2 rounded-lg font-bold text-sm uppercase tracking-wide ${
                        d.severity === 'red'
                          ? 'bg-red-50 text-red-700 border border-red-200'
                          : d.severity === 'amber'
                            ? 'bg-amber-50 text-amber-700 border border-amber-200'
                            : 'bg-green-50 text-green-700 border border-green-200'
                      }`}
                    >
                      {d.date}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{d.label}</h3>
                      <p className="text-gray-700 leading-relaxed">{d.detail}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* What IAM 14c includes */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              What IAM 14c includes
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              The four components of Identity &amp; Access Management 14c
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              IAM 14c (14.1.2.1) runs on WebLogic Server 14c, certified with Oracle Database
              19c/23c/23ai and JDK 17 (also certified with JDK 21). The four core components carry
              forward from 12c.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {COMPONENTS_14C.map(({ icon: Icon, title, detail }) => (
                <Card key={title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-accent" aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-1">{title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{detail}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* The 12c to 14c upgrade path */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              The upgrade path
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              The 12c PS4 → 14c upgrade, step by step
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Upgrades to 14c are supported from 12.2.1.4 (12c PS4), in-place and out-of-place, with
              WebLogic Server 14c underneath. Each step has its own scope and validation
              requirements.
            </p>
            <div className="space-y-5">
              {UPGRADE_PATH.map((s) => (
                <Card key={s.step} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-5">
                    <div className="w-12 h-12 rounded-lg bg-accent/15 flex items-center justify-center text-accent font-bold text-xl flex-shrink-0">
                      {s.step}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{s.title}</h3>
                      <p className="text-gray-700 leading-relaxed">
                        {s.weblogicLink ? (
                          <>
                            IAM 14c runs on Oracle WebLogic Server 14c, certified with Oracle
                            Database 19c/23c/23ai and JDK 17 (also certified with JDK 21). The 14c
                            middleware tier is established — see our{' '}
                            <Link
                              to="/solutions/oracle-weblogic-14c-upgrade"
                              className="text-primary hover:text-accent font-semibold"
                            >
                              WebLogic 14c upgrade page
                            </Link>{' '}
                            for the server-tier detail — as the foundation for the identity
                            components.
                          </>
                        ) : (
                          s.body
                        )}
                      </p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Component-by-component */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Component by component
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              What the upgrade means for OAM, OIG, and the directory tier
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Each component has its own migration concerns — SSO integrations, governance workflows
              and connectors, and directory data plus replication.
            </p>
            <div className="space-y-6">
              {COMPONENT_DETAIL.map(({ icon: Icon, component, what, detail }) => (
                <Card key={component} className="p-7 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-5">
                    <div className="w-12 h-12 rounded-lg bg-accent/15 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-6 h-6 text-accent" aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-1">{component}</h3>
                      <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
                        {what}
                      </p>
                      <p className="text-gray-700 leading-relaxed">{detail}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* How EZ Cloud delivers */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              How EZ Cloud delivers
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Founder-led IAM 12c → 14c upgrade delivery
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              EZ Cloud delivers Identity &amp; Access Management upgrade work founder-led. Andrew has
              hands-on IAM experience and reads the existing estate directly — OAM, OIG, OUD, and
              OID — then builds and validates the 14c estate before cutover.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {DELIVERY_STEPS.map(({ icon: Icon, title, body }) => (
                <Card key={title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-accent" aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-1">{title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{body}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
            <p className="text-center text-sm text-gray-500 mt-8 max-w-2xl mx-auto">
              Engagement scope and pricing are established per situation on a Decision Call.
            </p>
          </div>
        </section>

        {/* Cross-link to decision guide */}
        <section className="py-12 bg-accent/5 border-y border-accent/30">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
              Free resource
            </p>
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              The Oracle Fusion Middleware December 2026 Decision Guide
            </h3>
            <p className="text-gray-700 leading-relaxed mb-6 max-w-2xl mx-auto">
              The structured guide to the forward paths off Fusion Middleware 12c — including
              component-by-component 14c upgrade considerations — with diagnostic questions and an
              evaluation checklist.
            </p>
            <Link
              to="/resources/webcenter-decision-guide"
              className="inline-flex items-center font-semibold text-white bg-accent hover:bg-accent/90 transition-colors px-6 py-3 rounded-lg"
            >
              Read the Decision Guide
              <ArrowRight className="w-4 h-4 ml-2" aria-hidden />
            </Link>
          </div>
        </section>

        <WebCenterEngagementHistory />

        {/* FAQ */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Common questions
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-10">
              Direct answers on the IAM 12c → 14c upgrade
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f, i) => (
                <AccordionItem key={f.q} value={`item-${i}`}>
                  <AccordionTrigger className="text-left text-gray-900">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-gray-600">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-hero-gradient text-white">
          <div className="container mx-auto px-4 max-w-3xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Talk through your IAM 12c → 14c upgrade
            </h2>
            <p className="text-xl text-white/80 mb-8 leading-relaxed">
              A 30-minute Decision Call with {FOUNDER.name}, founder of EZ Cloud and a 25-year
              Oracle specialist with hands-on Identity &amp; Access Management experience. Walk
              through your OAM, OIG, OUD, and OID estate and the 14c upgrade considerations in the
              context of your specific implementation.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('iam-14c-cta')}
              >
                <CalendarClock className="mr-2 w-5 h-5" aria-hidden />
                Book an IAM Upgrade Decision Call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/solutions/oracle-fusion-middleware-12c-end-of-support">
                  See the full Fusion Middleware 12c guide
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default OracleIdentityManagement12cTo14cUpgrade;
